import { useWallet } from "@solana/wallet-adapter-react";
import { PhantomWalletName } from "@solana/wallet-adapter-phantom";
import { Link } from "react-router-dom";
import { Sigil, Hallmark } from "@/components/Sigil";
import { useAuth } from "@/components/providers/AuthProvider";

/**
 * Shown by RequireAuth when status !== "ready". Single primary action:
 * connect wallet. We only support Phantom, so we skip the wallet-selector
 * modal and call select() + connect() directly — one click, Phantom popup.
 * After Phantom connects, AuthProvider's effect signs the challenge.
 */
export function ConnectGate() {
  const { connected, connecting, disconnect, select, connect, wallet } = useWallet();
  const { status, error, signIn } = useAuth();

  // While Phantom is connecting OR while we're awaiting the post-connect
  // challenge signature, the button reads as busy.
  const busy = connecting || status === "signing" || (connected && status === "idle");

  const handleClick = async () => {
    // Wallet must be connected before we can sign anything. The previous logic
    // had an early-return for status==="error" that called signIn() directly,
    // which fired with publicKey=null when the operator had just switched
    // wallets — creating a "wallet not ready" loop on the Try again button.
    if (!connected) {
      try {
        // select() is a no-op if the right wallet is already selected.
        if (wallet?.adapter.name !== PhantomWalletName) {
          select(PhantomWalletName);
        }
        await connect();
        // After connect succeeds, AuthProvider's wallet effect picks up
        // the connection and triggers signIn() automatically.
      } catch (err) {
        // User rejected the popup, or Phantom is not installed.
        // The wallet adapter logs internally — we surface nothing extra here.
        console.error("[connect-gate] Phantom connect failed:", err);
      }
      return;
    }
    void signIn();
  };

  return (
    <div className="connect-gate">
      <Link to="/" className="connect-gate__brand">Altheia</Link>

      <div className="connect-gate__inner">
        <div className="connect-gate__sigil">
          <Sigil
            seed="connect-prompt"
            size={132}
            tone="bone"
            glow
            sweeping
            strokeWidth={1.1}
          />
        </div>
        <span className="kicker kicker--bronze connect-gate__kicker">⊙ Awaiting operator</span>
        <h1 className="connect-gate__headline">Connect a wallet to continue</h1>
        <p className="connect-gate__body">
          Your wallet is your operator identity. You'll sign a one-time challenge — no
          transaction, no spend. Devnet only for now.
        </p>

        {status === "error" && error && (
          <div className="connect-gate__err">
            <Hallmark size={11} tone="oxblood" /> {error}
          </div>
        )}

        <button
          type="button"
          className="btn btn--primary connect-gate__cta"
          onClick={handleClick}
          disabled={busy}
        >
          {status === "signing"
            ? "Signing in your wallet…"
            : connected && status === "idle"
              ? "Connecting…"
              : status === "error"
                ? "Try again"
                : (
                  <>
                    Connect wallet <span className="lp-cta__arrow">→</span>
                  </>
                )}
        </button>

        {connected && status !== "signing" && (
          <button
            type="button"
            className="connect-gate__disconnect kicker"
            onClick={() => void disconnect()}
          >
            Disconnect
          </button>
        )}

        <p className="connect-gate__note">
          New here? <Link to="/" className="connect-gate__link">See what Altheia does →</Link>
        </p>
      </div>

      <ConnectGateStyles />
    </div>
  );
}

function ConnectGateStyles() {
  return (
    <style>{`
      .connect-gate { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: var(--bg); padding: var(--s-6); position: relative; }
      .connect-gate__brand { position: absolute; top: var(--s-5); left: var(--s-6); font-family: var(--font-display); font-style: italic; font-size: 22px; color: var(--bone); letter-spacing: 0.01em; }
      .connect-gate__inner { display: flex; flex-direction: column; gap: var(--s-3); align-items: center; max-width: 520px; text-align: center; }

      /* Sigil — breathing heartbeat + initial entry burst, mirrors the assembler's climax */
      .connect-gate__sigil {
        margin-bottom: var(--s-3);
        display: inline-block;
        animation:
          connect-gate-burst 1800ms cubic-bezier(.4, 0, .2, 1) 200ms 1 forwards,
          connect-gate-breathe 4s ease-in-out 2000ms infinite;
        will-change: transform, filter;
      }
      @keyframes connect-gate-burst {
        0%   { transform: scale(0.92); filter: drop-shadow(0 0 0 transparent); opacity: 0; }
        45%  { transform: scale(1.06); filter: drop-shadow(0 0 28px var(--bronze-glow)); opacity: 1; }
        100% { transform: scale(1);    filter: drop-shadow(0 0 0 transparent); opacity: 1; }
      }
      @keyframes connect-gate-breathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.025); }
      }

      .connect-gate__kicker { margin-top: var(--s-2); }
      .connect-gate__headline { font: var(--type-display-section); margin: var(--s-2) 0 0; }
      .connect-gate__body { color: var(--slate); font: var(--type-body); margin: 0; max-width: 440px; }

      /* Primary CTA — bronze fill + pulse-on-rest. btn btn--primary supplies the
         base color; we override sizing + pulse for the connect-gate context. */
      .connect-gate__cta {
        position: relative;
        margin-top: var(--s-4);
        min-width: 260px;
        padding: 16px 36px;
        font-size: 14px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        background: var(--bronze);
        color: var(--text-on-ink);
        border: 1px solid var(--bronze);
        cursor: pointer;
        transition: background 160ms;
        animation: connect-gate-cta-pulse 2.6s ease-in-out 1200ms infinite;
        box-shadow: 0 0 0 0 var(--bronze-glow);
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .connect-gate__cta:hover:not(:disabled) { background: var(--bronze-deep); border-color: var(--bronze-deep); }
      .connect-gate__cta:disabled { opacity: 0.65; cursor: not-allowed; animation: none; }
      @keyframes connect-gate-cta-pulse {
        0%, 100% { box-shadow: 0 0 0 0 var(--bronze-glow); }
        50%      { box-shadow: 0 0 0 14px transparent; }
      }

      .connect-gate__err { color: var(--oxblood); font: var(--type-body-small); display: inline-flex; align-items: center; gap: var(--s-2); padding: var(--s-2) var(--s-3); border: 1px solid var(--oxblood); margin-top: var(--s-2); }
      .connect-gate__disconnect { background: none; border: 0; color: var(--slate); cursor: pointer; padding: var(--s-1) var(--s-2); margin-top: var(--s-1); }
      .connect-gate__disconnect:hover { color: var(--bone); }
      .connect-gate__note { color: var(--slate-2); font: var(--type-body-small); margin-top: var(--s-5); }
      .connect-gate__link { color: var(--bronze); border-bottom: 1px solid var(--line-soft); padding-bottom: 1px; }
      .connect-gate__link:hover { border-bottom-color: var(--bronze); }
    `}</style>
  );
}
